import React from 'react';
import { Box, Typography } from '@mui/material';
import type { PoplachTechnikaResponse, PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated';
import { formatCasHHmm } from '../utils/casUtils';
import { FitHeightBox } from './FitHeightBox';

interface ZpravyUTechnikyProps {
    posilovaTechnikaTexty?: string[];
    posledniZprava?: PosilovaNotifikaceLogResponse;
    technikaSources?: PoplachTechnikaResponse[];
    // Strop výšky bloku (např. '40%' výšky rodiče); písmo se zmenší, aby se vešel celý text.
    maxHeight?: string;
}

// Zprávy dostanou jen zbylé místo (rodič má strop výšky) a písmo se zmenšuje, dokud se vejdou
// všechny celé, nejvýš ale na 16 px (čitelnost ze 3 m). Co se nevejde ani tak, dostane náznak „…"
// (FitHeightBox); nic se potichu neukrajuje: kiosek se nedá posouvat ani kliknout.
const text = { fontSize: 'inherit', fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' } as const;

export const ZpravyUTechniky: React.FC<ZpravyUTechnikyProps> = ({
    posilovaTechnikaTexty, posledniZprava, technikaSources, maxHeight,
}) => {
    const poznamky = (technikaSources ?? []).filter(t => t.zdroj === 'VELITEL' && t.poznamka);
    const maPosilu = !!posilovaTechnikaTexty && posilovaTechnikaTexty.length > 0;
    if (!maPosilu && !posledniZprava && poznamky.length === 0) return null;

    const posilaText = maPosilu ? `Povolána další technika: ${posilovaTechnikaTexty!.join(', ')}` : '';
    const posledniText = posledniZprava
        ? `Poslední zpráva: ${posledniZprava.text || 'Zpráva'} (${formatCasHHmm(posledniZprava.createdAt)})`
        : '';
    const fitKey = [posilaText, posledniText, ...poznamky.map(t => `${t.volaciZnak}:${t.poznamka}`)].join('|');

    return (
        <FitHeightBox data-testid="zpravy-u-techniky" maxFontPx={20} fitKey={fitKey} orezRadky sx={{ flex: '0 1 auto', mt: 1, bgcolor: 'background.paper', ...(maxHeight ? { maxHeight } : {}) }}>
            {maPosilu && (
                <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: 'info.main', color: '#fff' }}>
                    <Typography data-testid="zprava-posila" sx={text}>{posilaText}</Typography>
                </Box>
            )}
            {posledniZprava && (
                <Box sx={{ mt: 0.5, p: 0.75, borderRadius: 1, bgcolor: posledniZprava.kriticka ? 'error.main' : 'warning.main', color: '#fff' }}>
                    <Typography data-testid="zprava-posledni" sx={text}>{posledniText}</Typography>
                </Box>
            )}
            {poznamky.length > 0 && (
                <Box sx={{ mt: 0.5, p: 0.75, borderRadius: 1, bgcolor: 'grey.700', color: '#fff' }}>
                    {poznamky.map((t, i) => (
                        <Typography key={i} data-testid="zprava-poznamka" sx={{ ...text, fontWeight: 500 }}>
                            Poznámka ({t.nazev} - {t.volaciZnak}): {t.poznamka}
                        </Typography>
                    ))}
                </Box>
            )}
        </FitHeightBox>
    );
};
